import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { X, Upload } from 'lucide-react';
import { ModalHeader, ModalBody, ModalFooter } from '@/components/ui/modal';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const feedbackSchema = z.object({
  subject: z.string().min(1, 'Assunto é obrigatório'),
  message: z.string().min(10, 'Mensagem deve ter pelo menos 10 caracteres'),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

interface FeedbackFormProps {
  onClose: () => void;
}

export const FeedbackForm = ({ onClose }: FeedbackFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema)
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length !== files.length) {
      toast.error('Apenas imagens são permitidas');
      return;
    }

    const totalSize = [...attachments, ...imageFiles].reduce((sum, file) => sum + file.size, 0);
    const maxSize = 10 * 1024 * 1024;

    if (totalSize > maxSize) {
      toast.error('O tamanho total dos anexos não pode exceder 10MB');
      return;
    }

    setAttachments(prev => [...prev, ...imageFiles]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: FeedbackFormData) => {
    setIsSubmitting(true);

    try {
      const attachmentData = await Promise.all(
        attachments.map(async (file) => {
          const arrayBuffer = await file.arrayBuffer();
          const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
          return {
            filename: file.name,
            content: base64,
            type: file.type
          };
        })
      );

      const { error } = await supabase.functions.invoke('send-feedback', {
        body: {
          subject: data.subject,
          message: data.message,
          attachments: attachmentData
        }
      });

      if (error) throw error;

      toast.success('Feedback enviado com sucesso!');
      reset();
      setAttachments([]);
      onClose();
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
      toast.error('Erro ao enviar feedback. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <ModalHeader
        title="Feedback e Sugestões"
        description="Compartilhe sua opinião para nos ajudar a melhorar a plataforma"
        icon={Upload}
        onClose={onClose}
      />
      <ModalBody>
        <form id="feedback-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="subject">Assunto <span className="text-destructive ml-1">*</span></Label>
            <Input
              id="subject"
              {...register('subject')}
              placeholder="Descreva brevemente o assunto do seu feedback"
              disabled={isSubmitting}
              className="rounded-xl"
            />
            {errors.subject && (
              <p className="text-sm text-destructive">{errors.subject.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Mensagem <span className="text-destructive ml-1">*</span></Label>
            <Textarea
              id="message"
              {...register('message')}
              placeholder="Descreva detalhadamente seu feedback, sugestão ou problema..."
              rows={5}
              disabled={isSubmitting}
              className="rounded-xl"
            />
            {errors.message && (
              <p className="text-sm text-destructive">{errors.message.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Anexos (Opcional)</Label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={isSubmitting}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center space-y-2"
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Clique para anexar imagens (máx. 10MB total)
                </p>
              </label>
            </div>

            {attachments.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Anexos:</p>
                {attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                    <span className="text-sm truncate">{file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(index)}
                      disabled={isSubmitting}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>
      </ModalBody>
      <ModalFooter
        primaryAction={{
          label: isSubmitting ? 'Enviando...' : 'Enviar Feedback',
          type: 'submit',
          formId: 'feedback-form',
          isLoading: isSubmitting
        }}
        secondaryAction={{
          label: 'Cancelar',
          onClick: onClose,
          disabled: isSubmitting
        }}
      />
    </div>
  );
};
